import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { customersAPI, customersKycAPI } from '../services/api';

const BLANK_FORM = {
  name: '', phone: '',
  aadhaarNumber: '', panNumber: '',
  isWitnessContact: false
};
const BLANK_FILES = { customerPhoto: null, aadhaarFront: null, aadhaarBack: null, pan: null };

export default function Customers() {
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [form, setForm]             = useState(BLANK_FORM);
  const [files, setFiles]           = useState(BLANK_FILES);
  const [adding, setAdding]         = useState(false);
  const [err, setErr]               = useState('');
  const [kycErr, setKycErr]         = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Cross-branch import prompt state
  const [importPrompt, setImportPrompt] = useState(null); // { message, importData }

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await customersAPI.list({ search: q, limit: 100 });
      setCustomers(res.data.customers || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search), 280);
    return () => clearTimeout(t);
  }, [search]);

  const uploadKyc = async (custId) => {
    const hasFiles = !!(files.customerPhoto || files.aadhaarFront || files.aadhaarBack || files.pan);
    if (!hasFiles) return;
    const fd = new FormData();
    if (files.customerPhoto) fd.append('customerPhoto', files.customerPhoto);
    if (files.aadhaarFront)  fd.append('aadhaarFront',  files.aadhaarFront);
    if (files.aadhaarBack)   fd.append('aadhaarBack',   files.aadhaarBack);
    if (files.pan)           fd.append('pan',           files.pan);
    try {
      await customersKycAPI.upload(custId, fd);
    } catch (e) {
      setKycErr('Customer saved, but KYC documents could not be uploaded. Please try uploading from the customer\'s Profile page.');
    }
  };

  const doCreate = async (overrideForm = null) => {
    const f = overrideForm || form;
    setErr('');
    setKycErr('');
    setAdding(true);
    try {
      const res = await customersAPI.create({
        name:             f.name.trim(),
        phone:            f.phone.trim(),
        aadhaarNumber:    f.aadhaarNumber.replace(/\D/g, '').slice(0, 12),
        panNumber:        f.panNumber.trim().toUpperCase(),
        isWitnessContact: f.isWitnessContact,
        forceCreate:      !!overrideForm // true when user confirmed import
      });

      if (res.data.importAvailable) {
        // Backend says phone exists in another branch — ask user to confirm import
        setImportPrompt({
          message:    res.data.message,
          importData: res.data.importData,
          phone:      f.phone.trim()
        });
        setAdding(false);
        return;
      }

      const cust = res.data.customer;
      if (!cust?._id) {
        setErr('Customer created but could not read ID.');
        setAdding(false);
        return;
      }

      await uploadKyc(cust._id);
      setForm(BLANK_FORM);
      setFiles(BLANK_FILES);
      await load(search);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to add customer';
      setErr(msg);
    } finally {
      setAdding(false);
    }
  };

  const addCustomer = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setErr('Name and phone are required.');
      return;
    }
    doCreate();
  };

  const confirmImport = () => {
    if (!importPrompt) return;
    const imp = importPrompt.importData || {};
    const merged = {
      ...form,
      name:          imp.name          || form.name,
      aadhaarNumber: imp.aadhaarNumber || form.aadhaarNumber,
      panNumber:     imp.panNumber     || form.panNumber,
      phone:         importPrompt.phone || form.phone
    };
    setImportPrompt(null);
    doCreate(merged);
  };

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0 fw-700">Customers</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Add KYC documents in one step · mark witness contacts for faster EMI sales
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <input
            className="form-control"
            style={{ maxWidth: 300 }}
            placeholder="Search name or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowAddForm(s => !s)}
          >
            {showAddForm ? 'Close' : '+ Add Customer'}
          </button>
        </div>
      </div>

      {/* Cross-branch import prompt */}
      {importPrompt && (
        <div className="alert alert-warning d-flex align-items-start gap-3 mb-3" style={{ borderRadius: 12 }}>
          <span style={{ fontSize: 22 }}>⚠️</span>
          <div className="flex-grow-1">
            <strong>Phone found in another branch</strong>
            <div className="mt-1" style={{ fontSize: 14 }}>{importPrompt.message}</div>
            <div className="mt-2 d-flex gap-2 flex-wrap">
              <button className="btn btn-sm btn-warning" onClick={confirmImport} disabled={adding}>
                {adding ? 'Creating…' : 'Yes, import details & create here'}
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setImportPrompt(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="card adm-card mb-3">
          <div className="card-header">Add customer</div>
          <div className="card-body">
            <form onSubmit={addCustomer}>
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Full name *</label>
                  <input className="form-control" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone *</label>
                  <input className="form-control" value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>

              {/* 1) First: customer photo */}
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Customer photo (sabse pehle)</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="form-control form-control-sm"
                    onChange={e => setFiles(f => ({ ...f, customerPhoto: e.target.files[0] || null }))}
                  />
                </div>
              </div>

              {/* 2) Aadhaar number + aadhaar pic */}
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">Aadhaar number (12 digits)</label>
                  <input
                    className="form-control"
                    inputMode="numeric"
                    maxLength={12}
                    value={form.aadhaarNumber}
                    onChange={e => setForm({ ...form, aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Aadhaar front pic</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="form-control form-control-sm"
                    onChange={e => setFiles(f => ({ ...f, aadhaarFront: e.target.files[0] || null }))}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Aadhaar back pic (optional)</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="form-control form-control-sm"
                    onChange={e => setFiles(f => ({ ...f, aadhaarBack: e.target.files[0] || null }))}
                  />
                </div>
              </div>

              {/* 3) PAN number + PAN pic */}
              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label">PAN number</label>
                  <input
                    className="form-control"
                    value={form.panNumber}
                    onChange={e => setForm({ ...form, panNumber: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">PAN card pic</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="form-control form-control-sm"
                    onChange={e => setFiles(f => ({ ...f, pan: e.target.files[0] || null }))}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="form-check mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="addWitnessMark"
                    checked={form.isWitnessContact}
                    onChange={e => setForm({ ...form, isWitnessContact: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="addWitnessMark">
                    Quick reference contact (profile se anytime on/off)
                    <span className="text-muted ms-1" style={{ fontSize: 12 }}>(customer remains normal customer)</span>
                  </label>
                </div>
                <button className="btn btn-primary" type="submit" disabled={adding}>
                  {adding ? 'Saving…' : 'Add customer'}
                </button>
              </div>
            </form>

            {err && <div className="alert-soft danger mt-2">{err}</div>}
            {kycErr && (
              <div className="alert-soft warning mt-2">
                <strong>Note:</strong> {kycErr}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="table-card">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>KYC</th>
                <th>Total spent</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {!loading && customers.length === 0 && (
                <tr><td colSpan="5" className="text-center text-muted py-4">No customers yet.</td></tr>
              )}
              {customers.map(c => {
                const kyc = c.kycSummary || {};
                return (
                  <tr key={c._id}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.createdAt && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          Added {new Date(c.createdAt).toLocaleDateString('en-IN')}
                        </div>
                      )}
                    </td>
                    <td>{c.phone}</td>
                    <td>
                      <span className={`chip ${kyc.emiKycReady ? 'badge-kyc-complete' : 'badge-kyc-pending'}`}>
                        {kyc.emiKycReady ? 'EMI KYC ready' : 'EMI KYC pending'}
                      </span>
                    </td>
                    <td>₹{Number(c.totalSpent || 0).toLocaleString('en-IN')}</td>
                    <td className="text-end">
                      <Link
                        to={`/customers/${encodeURIComponent(c._id)}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        Profile
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {loading && (
                <tr><td colSpan="5" className="text-center py-3">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
